import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { 
  Crown, 
  Clock, 
  Award, 
  Star, 
  CheckCircle, 
  BookOpen, 
  Globe,
  Mail,
  ArrowRight,
  Target,
  Shield,
  TrendingUp,
  Users,
  Home,
  ArrowLeft
} from "lucide-react";

interface EmbedCSLProps {
  compact?: boolean;
  showHero?: boolean;
  showQuickFacts?: boolean;
  showLearningAreas?: boolean;
  showRequirements?: boolean;
  showBenefits?: boolean;
  showIdealFor?: boolean;
  showNextSteps?: boolean;
  maxWidth?: string;
}

const EmbedCSL = ({ 
  compact = false,
  showHero = true,
  showQuickFacts = true,
  showLearningAreas = true,
  showRequirements = true,
  showBenefits = true,
  showIdealFor = true,
  showNextSteps = true,
  maxWidth = "6xl"
}: EmbedCSLProps) => {
  const keyAreas = [
    {
      icon: <Crown className="w-6 h-6" />,
      title: "Advanced Expertise",
      description: "Mastery of both technical and strategic competencies in converged security"
    },
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: "Strategic Insight",
      description: "Executive-level strategic thinking for enterprise security transformation"
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Operational Readiness",
      description: "Proven capability to lead security initiatives across critical infrastructure"
    },
    {
      icon: <Target className="w-6 h-6" />,
      title: "Enterprise Leadership",
      description: "Qualified to lead security initiatives in enterprise and smart environments"
    },
    {
      icon: <Globe className="w-6 h-6" />,
      title: "Cross-Domain Integration",
      description: "Seamless integration across physical, cyber, and operational security domains"
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Team Excellence",
      description: "Advanced skills in leading diverse, cross-functional security teams"
    }
  ];

  const benefits = [
    "Highest credential awarded by the Converged Security Institute",
    "Recognition of advanced expertise and strategic insight",
    "Operational readiness for enterprise leadership roles",
    "Automatic award upon completing CSCE and CSP programs",
    "No additional examination or costs required",
    "Industry recognition for comprehensive security mastery"
  ];

  const idealFor = [
    "CSCE and CSP certified professionals",
    "Senior security leaders managing enterprise environments",
    "Strategic security consultants and advisors",
    "C-level executives overseeing security transformation",
    "Security architects designing enterprise solutions",
    "Leaders driving converged security initiatives"
  ];

  const requirements = [
    {
      title: "CSCE Certification",
      description: "Valid Converged Security & Cybersecurity Expert certification",
      icon: <CheckCircle className="w-5 h-5 text-yellow-600" />
    },
    {
      title: "CSP Certification", 
      description: "Valid Converged Security Professional certification",
      icon: <CheckCircle className="w-5 h-5 text-yellow-600" />
    },
    {
      title: "Program Completion",
      description: "Verified completion of both prerequisite programs",
      icon: <CheckCircle className="w-5 h-5 text-yellow-600" />
    },
    {
      title: "Automatic Award",
      description: "CSL awarded automatically upon meeting all requirements",
      icon: <Star className="w-5 h-5 text-yellow-600" />
    }
  ];

  return (
    <div className="bg-gradient-to-br from-yellow-50 to-orange-50 font-sans relative">
      {/* Navigation Bar - Upper Right */}
      <div className="absolute top-4 right-6 flex gap-2 z-10">
        <Button 
          variant="outline" 
          size="sm"
          className="bg-white border-gray-300 hover:bg-gray-50"
          onClick={() => window.history.back()}
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back
        </Button>
        <Button 
          variant="outline" 
          size="sm"
          className="bg-white border-gray-300 hover:bg-gray-50"
          onClick={() => window.open('https://convergedsecurity.es/', '_blank')}
        >
          <Home className="w-4 h-4 mr-1" />
          Home
        </Button>
      </div>
      {/* Hero Section */}
      {showHero && (
        <div className="bg-gradient-to-r from-yellow-600 to-orange-600 text-white py-12">
          <div className={`max-w-${maxWidth} mx-auto px-4`}>
            <div className={`grid ${compact ? 'lg:grid-cols-1 text-center' : 'lg:grid-cols-2'} gap-8 items-center`}>
              <div>
                <Badge className="bg-white/20 text-white border-white/30 mb-3">
                  Leadership Credential
                </Badge>
                <h1 className={`${compact ? 'text-3xl md:text-4xl' : 'text-4xl md:text-5xl'} font-bold mb-4`}>
                  CSL
                </h1>
                <h2 className={`${compact ? 'text-xl md:text-2xl' : 'text-2xl md:text-3xl'} font-semibold mb-3 text-yellow-100`}>
                  Converged Security Leader
                </h2>
                <p className={`${compact ? 'text-base' : 'text-lg'} text-yellow-100 mb-6 leading-relaxed`}>
                  The highest credential recognizing advanced expertise, strategic insight, and operational readiness.
                </p>
                {!compact && (
                  <div className="flex flex-wrap gap-3">
                    <Button 
                      size={compact ? "default" : "lg"} 
                      className="bg-white text-yellow-600 hover:bg-yellow-50"
                      onClick={() => window.open('mailto:info@convergedsecurity.es?subject=CSL Certification Inquiry&body=Hello, I would like to learn more about the CSL (Converged Security Leader) certification. I have completed both CSCE and CSP programs. Please provide information about the automatic award process.', '_blank')}
                    >
                      <Mail className="w-4 h-4 mr-2" />
                      Contact Us
                    </Button>
                  </div>
                )}
              </div>
              {!compact && (
                <div className="flex justify-center">
                  <div className="relative">
                    <img 
                      src="./images/leader.png" 
                      alt="CSL Certification Badge"
                      className="w-60 h-60 object-contain drop-shadow-2xl"
                    />
                    <div className="absolute -inset-4 bg-gradient-to-r from-yellow-400/20 to-orange-400/20 rounded-full blur-xl"></div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Quick Facts */}
      {showQuickFacts && (
        <div className={`${compact ? 'py-8' : 'py-12'} bg-white`}>
          <div className={`max-w-${maxWidth} mx-auto px-4`}>
            <div className={`grid ${compact ? 'grid-cols-2 md:grid-cols-4' : 'md:grid-cols-4'} gap-4`}>
              <Card className="text-center border-yellow-200">
                <CardContent className={compact ? "pt-4" : "pt-6"}>
                  <Star className={`${compact ? 'w-6 h-6' : 'w-8 h-8'} text-yellow-600 mx-auto mb-2`} />
                  <h3 className={`font-bold ${compact ? 'text-lg' : 'text-2xl'} text-gray-900`}>No Cost</h3>
                  <p className={`${compact ? 'text-xs' : 'text-sm'} text-gray-600`}>Additional Fee</p>
                </CardContent>
              </Card>
              <Card className="text-center border-yellow-200">
                <CardContent className={compact ? "pt-4" : "pt-6"}>
                  <Clock className={`${compact ? 'w-6 h-6' : 'w-8 h-8'} text-yellow-600 mx-auto mb-2`} />
                  <h3 className={`font-bold ${compact ? 'text-lg' : 'text-2xl'} text-gray-900`}>3 Years</h3>
                  <p className={`${compact ? 'text-xs' : 'text-sm'} text-gray-600`}>Validity Period</p>
                </CardContent>
              </Card>
              <Card className="text-center border-yellow-200">
                <CardContent className={compact ? "pt-4" : "pt-6"}>
                  <Users className={`${compact ? 'w-6 h-6' : 'w-8 h-8'} text-yellow-600 mx-auto mb-2`} />
                  <h3 className={`font-bold ${compact ? 'text-sm' : 'text-xl'} text-gray-900`}>CSCE + CSP</h3>
                  <p className={`${compact ? 'text-xs' : 'text-sm'} text-gray-600`}>Prerequisites</p>
                </CardContent>
              </Card>
              <Card className="text-center border-yellow-200">
                <CardContent className={compact ? "pt-4" : "pt-6"}>
                  <Award className={`${compact ? 'w-6 h-6' : 'w-8 h-8'} text-yellow-600 mx-auto mb-2`} />
                  <h3 className={`font-bold ${compact ? 'text-lg' : 'text-2xl'} text-gray-900`}>Automatic</h3>
                  <p className={`${compact ? 'text-xs' : 'text-sm'} text-gray-600`}>Award Process</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      )}

      {/* Key Competencies */}
      {showLearningAreas && (
        <div className={compact ? 'py-8' : 'py-12'}>
          <div className={`max-w-${maxWidth} mx-auto px-4`}>
            <div className="text-center mb-8">
              <h2 className={`${compact ? 'text-2xl md:text-3xl' : 'text-3xl md:text-4xl'} font-bold text-gray-900 mb-3`}>
                Leadership Excellence
              </h2>
              <p className={`${compact ? 'text-base' : 'text-lg'} text-gray-600 max-w-2xl mx-auto`}>
                CSL represents the pinnacle of converged security expertise
              </p>
            </div>
            
            <div className={`grid ${compact ? 'md:grid-cols-2' : 'md:grid-cols-2 lg:grid-cols-3'} gap-6`}>
              {keyAreas.slice(0, compact ? 4 : 6).map((area, index) => (
                <Card key={index} className="hover:shadow-lg transition-shadow duration-300 border-yellow-100">
                  <CardHeader className={compact ? "pb-2" : "pb-3"}>
                    <div className="flex items-center gap-3 mb-2">
                      <div className={`p-2 bg-yellow-100 rounded-lg text-yellow-600`}>
                        {React.cloneElement(area.icon, { 
                          className: compact ? "w-4 h-4" : "w-6 h-6" 
                        })}
                      </div>
                      <CardTitle className={compact ? "text-base" : "text-lg"}>{area.title}</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent className={compact ? "pt-0" : ""}>
                    <p className={`${compact ? 'text-sm' : 'text-base'} text-gray-600`}>{area.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Requirements Section */}
      {showRequirements && (
        <div className={`${compact ? 'py-8' : 'py-12'} bg-gradient-to-r from-yellow-600 to-orange-600 text-white`}>
          <div className={`max-w-${maxWidth} mx-auto px-4`}>
            <div className="text-center mb-8">
              <h2 className={`${compact ? 'text-2xl md:text-3xl' : 'text-3xl md:text-4xl'} font-bold mb-3`}>
                Certification Requirements
              </h2>
              <p className={`${compact ? 'text-base' : 'text-lg'} text-yellow-100 max-w-2xl mx-auto`}>
                CSL is awarded automatically upon verified completion of prerequisites
              </p>
            </div>
            
            <div className={`grid ${compact ? 'md:grid-cols-2' : 'md:grid-cols-2'} gap-6`}>
              {requirements.map((req, index) => (
                <Card key={index} className="bg-white/10 backdrop-blur-sm border-white/20">
                  <CardContent className={compact ? "pt-4" : "pt-6"}>
                    <div className="flex items-start gap-4">
                      <div className="p-2 bg-white/20 rounded-lg">
                        {req.icon}
                      </div>
                      <div>
                        <h3 className={`${compact ? 'text-lg' : 'text-xl'} font-bold mb-2 text-white`}>{req.title}</h3>
                        <p className={`${compact ? 'text-sm' : 'text-base'} text-yellow-100`}>{req.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Program Benefits */}
      {showBenefits && (
        <div className={`${compact ? 'py-8' : 'py-12'} bg-white`}>
          <div className={`max-w-${maxWidth} mx-auto px-4`}>
            <div className={`grid ${compact ? 'lg:grid-cols-1' : 'lg:grid-cols-2'} gap-8 items-center`}>
              <div>
                <h2 className={`${compact ? 'text-2xl md:text-3xl' : 'text-3xl md:text-4xl'} font-bold text-gray-900 mb-4`}>
                  Why CSL Matters
                </h2>
                <p className={`${compact ? 'text-base' : 'text-lg'} text-gray-600 mb-6`}>
                  CSL represents the culmination of your converged security journey.
                </p>
                <div className="space-y-3">
                  {benefits.slice(0, compact ? 4 : 6).map((benefit, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <CheckCircle className={`${compact ? 'w-4 h-4' : 'w-5 h-5'} text-yellow-600 flex-shrink-0 mt-0.5`} />
                      <p className={`${compact ? 'text-sm' : 'text-base'} text-gray-700`}>{benefit}</p>
                    </div>
                  ))}
                </div>
              </div>
              {!compact && (
                <div className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-2xl p-6 border border-yellow-200">
                  <h3 className="text-xl font-bold mb-4 text-gray-900">Award Process</h3>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-4 h-4 text-yellow-600" />
                      <span className="text-sm">Automatic verification of CSCE completion</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-4 h-4 text-yellow-600" />
                      <span className="text-sm">Automatic verification of CSP completion</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Award className="w-4 h-4 text-yellow-600" />
                      <span className="text-sm">CSL awarded within 24 hours</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Globe className="w-4 h-4 text-yellow-600" />
                      <span className="text-sm">Digital certificate and badge issued</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Ideal Candidates */}
      {showIdealFor && (
        <div className={`${compact ? 'py-8' : 'py-12'} bg-gradient-to-br from-yellow-50 to-orange-50`}>
          <div className={`max-w-${maxWidth} mx-auto px-4`}>
            <div className="text-center mb-8">
              <h2 className={`${compact ? 'text-2xl md:text-3xl' : 'text-3xl md:text-4xl'} font-bold text-gray-900 mb-3`}>
                Perfect For
              </h2>
              <p className={`${compact ? 'text-base' : 'text-lg'} text-gray-600 max-w-2xl mx-auto`}>
                CSL is designed for professionals who have demonstrated comprehensive mastery
              </p>
            </div>
            
            <div className={`grid ${compact ? 'md:grid-cols-2' : 'md:grid-cols-2 lg:grid-cols-3'} gap-4`}>
              {idealFor.slice(0, compact ? 4 : 6).map((candidate, index) => (
                <Card key={index} className="border-yellow-100 hover:border-yellow-300 transition-colors">
                  <CardContent className={compact ? "pt-4" : "pt-6"}>
                    <div className="flex items-start gap-3">
                      <ArrowRight className={`${compact ? 'w-4 h-4' : 'w-5 h-5'} text-yellow-600 flex-shrink-0 mt-0.5`} />
                      <p className={`${compact ? 'text-sm' : 'text-base'} text-gray-700`}>{candidate}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Next Steps */}
      {showNextSteps && (
        <div className={`${compact ? 'py-8' : 'py-12'} bg-white`}>
          <div className={`max-w-${compact ? '3xl' : '4xl'} mx-auto px-4 text-center`}>
            <h2 className={`${compact ? 'text-2xl md:text-3xl' : 'text-3xl md:text-4xl'} font-bold text-gray-900 mb-4`}>
              Ready for Leadership Recognition?
            </h2>
            <p className={`${compact ? 'text-base' : 'text-lg'} text-gray-600 mb-6`}>
              Complete both CSCE and CSP programs to automatically receive your CSL certification
            </p>
            
            <Card className="border-yellow-200 shadow-lg">
              <CardContent className={compact ? "pt-6" : "pt-8"}>
                <div className={`grid ${compact ? 'grid-cols-3' : 'md:grid-cols-3'} gap-4 mb-6`}>
                  <div className="text-center">
                    <div className={`${compact ? 'w-8 h-8' : 'w-12 h-12'} bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-2`}>
                      <span className={`text-yellow-600 font-bold ${compact ? 'text-sm' : ''}`}>1</span>
                    </div>
                    <h3 className={`font-semibold mb-1 ${compact ? 'text-sm' : ''}`}>Complete CSCE</h3>
                    <p className={`${compact ? 'text-xs' : 'text-sm'} text-gray-600`}>Earn foundational certification</p>
                  </div>
                  <div className="text-center">
                    <div className={`${compact ? 'w-8 h-8' : 'w-12 h-12'} bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-2`}>
                      <span className={`text-yellow-600 font-bold ${compact ? 'text-sm' : ''}`}>2</span>
                    </div>
                    <h3 className={`font-semibold mb-1 ${compact ? 'text-sm' : ''}`}>Complete CSP</h3>
                    <p className={`${compact ? 'text-xs' : 'text-sm'} text-gray-600`}>Advance to strategic leadership</p>
                  </div>
                  <div className="text-center">
                    <div className={`${compact ? 'w-8 h-8' : 'w-12 h-12'} bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-2`}>
                      <span className={`text-yellow-600 font-bold ${compact ? 'text-sm' : ''}`}>3</span>
                    </div>
                    <h3 className={`font-semibold mb-1 ${compact ? 'text-sm' : ''}`}>Receive CSL</h3>
                    <p className={`${compact ? 'text-xs' : 'text-sm'} text-gray-600`}>Automatic award within 24 hours</p>
                  </div>
                </div>
                
                <div className="flex flex-wrap justify-center gap-3">
                  <Button 
                    size={compact ? "default" : "lg"} 
                    className="bg-gradient-to-r from-yellow-600 to-orange-600 hover:from-yellow-700 hover:to-orange-700"
                    onClick={() => window.open('mailto:info@convergedsecurity.es?subject=CSL Certification Status Inquiry&body=Hello, I would like to check my eligibility for the CSL (Converged Security Leader) certification. I have completed CSCE and/or CSP programs. Please verify my status and provide next steps.', '_blank')}
                  >
                    <Mail className={`${compact ? 'w-4 h-4' : 'w-5 h-5'} mr-2`} />
                    Check Eligibility
                  </Button>
                  {!compact && (
                    <Button 
                      size="lg" 
                      variant="outline" 
                      className="border-yellow-300 text-yellow-600 hover:bg-yellow-50"
                      onClick={() => window.open('mailto:info@convergedsecurity.es?subject=CSL Brochure Request&body=Hello, I would like to download the CSL (Converged Security Leader) certification brochure. Please send me the detailed program information.', '_blank')}
                    >
                      <BookOpen className="w-5 h-5 mr-2" />
                      Download Brochure
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmbedCSL;